import { AlertCircle, CalendarClock, Loader, Play, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { buildDailyFocus } from '@/features/memorization/daily-focus';
import type { FocusItem, FocusReason } from '@/features/memorization/daily-focus';
import { useTargets } from '@/features/memorization/hooks';
import { useSurahLookup } from '@/features/quran/hooks';
import type { SurahView } from '@/features/quran/types';

/**
 * "Fokus Hari Ini" (ADR-032) - the short list of what to work on next,
 * ranked by `buildDailyFocus` from the learner's own targets.
 *
 * Reads the same first page of `GET /memorization/targets` the list below
 * it already renders, so the suggestions cost no additional request. Every
 * row states the reason it is there and links to the target it names -
 * there is no suggestion this card can make that the reader cannot trace
 * back to their own data.
 */

/** How many targets to rank over - the same first page `TargetList` shows. */
const SOURCE_PAGE_SIZE = 20;

const REASON_ICONS: Readonly<Record<FocusReason, LucideIcon>> = {
  NEEDS_REVISION: AlertCircle,
  OVERDUE: CalendarClock,
  DUE_SOON: CalendarClock,
  CONTINUE: Loader,
  NOT_STARTED: Play,
};

const REASON_LABELS: Readonly<Record<FocusReason, string>> = {
  NEEDS_REVISION: 'Perlu perbaikan',
  OVERDUE: 'Lewat tenggat',
  DUE_SOON: 'Segera',
  CONTINUE: 'Lanjutkan',
  NOT_STARTED: 'Mulai',
};

function reasonVariant(reason: FocusReason): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (reason === 'NEEDS_REVISION' || reason === 'OVERDUE') {
    return 'destructive';
  }
  if (reason === 'DUE_SOON') {
    return 'secondary';
  }
  return 'outline';
}

function reasonDetail(item: FocusItem): string {
  const days = item.daysUntilTarget;

  switch (item.reason) {
    case 'NEEDS_REVISION':
      return 'Ada koreksi yang menunggu diperbaiki.';
    case 'OVERDUE':
      return days === null
        ? 'Tenggatnya sudah lewat.'
        : `Tenggatnya lewat ${String(Math.abs(days))} hari.`;
    case 'DUE_SOON':
      if (days === 0) {
        return 'Tenggatnya hari ini.';
      }
      return days === null ? 'Tenggatnya sudah dekat.' : `Tinggal ${String(days)} hari lagi.`;
    case 'CONTINUE':
      return 'Sudah berjalan - teruskan sedikit lagi hari ini.';
    case 'NOT_STARTED':
    default:
      return 'Belum ada setoran untuk target ini.';
  }
}

function surahTitle(surah: SurahView | undefined, number: number): string {
  if (surah === undefined) {
    return `Surah ${String(number)}`;
  }
  return `${String(number)}. ${surah.nameLatin}`;
}

function ayahRange(startAyah: number, endAyah: number): string {
  if (startAyah === endAyah) {
    return `Ayat ${String(startAyah)}`;
  }
  return `Ayat ${String(startAyah)}-${String(endAyah)}`;
}

export function DailyFocusCard({ studentId }: { readonly studentId: string }): JSX.Element | null {
  const { data, isLoading, isError } = useTargets(studentId, 1, SOURCE_PAGE_SIZE);
  const surahs = useSurahLookup();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fokus Hari Ini</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  // A failure here must not bury the page: the target list below renders
  // its own error, and a second copy of it would say nothing new.
  if (isError || data === undefined) {
    return null;
  }

  const items = buildDailyFocus(data.items, new Date());

  if (items.length === 0) {
    return null;
  }

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="size-4 text-primary" aria-hidden="true" />
          Fokus Hari Ini
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <ul className="flex flex-col gap-2">
          {items.map((item) => {
            const Icon = REASON_ICONS[item.reason];
            const surah = surahs.get(item.surah);

            return (
              <li key={item.targetId}>
                <Link to={`/students/${studentId}/targets/${item.targetId}`}>
                  <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-3 transition-colors hover:bg-secondary/50">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="flex flex-wrap items-baseline gap-x-2">
                          <span className="text-sm font-medium">
                            {surahTitle(surah, item.surah)}
                          </span>
                          {surah !== undefined && (
                            <span lang="ar" dir="rtl" className="text-sm text-muted-foreground">
                              {surah.nameArabic}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {ayahRange(item.startAyah, item.endAyah)} · {reasonDetail(item)}
                        </span>
                      </div>
                    </div>
                    <Badge variant={reasonVariant(item.reason)}>{REASON_LABELS[item.reason]}</Badge>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Disusun dari target Anda sendiri - urutannya mengikuti koreksi yang menunggu, lalu
          tenggat terdekat. Ini saran, bukan keharusan.
        </p>
      </CardContent>
    </Card>
  );
}
